/**
 * TimelineCSSSync — the CSS/DOM-element sync layer for the home timeline.
 *
 * Owns the CSSElement table (intro para + screens/spaces/systems HTML blocks),
 * the per-tick class/transform sync (-show classes, translate3d tracking with
 * drag parallax, the --home-ui-width / --screens-tv-edge custom properties),
 * the explore-label text-scramble hide/show and the projects-click UI prompt.
 *
 * IMPORTANT: the bodies are a verbatim extraction from HomeTimeline — the
 * fastdom measure/mutate phasing, the direct (un-batched) opacity write on the
 * screens element, and the single `screenX` binding shared by every queued
 * mutate closure are load-bearing quirks; do not re-batch or re-scope them.
 * Frame values (progress, velocity, containerX, inner/virtual widths) are
 * sampled from the timeline via narrow deps at the top of tick().
 *
 * Screens-TV CSS metrics: previously read via HomeScreens.getTopScreenWidth()
 * / getRightScreenX(); per the revised design they are computed here from the
 * SAME shared item sprites (screensTVTopLeft / screensTVTopRight) that
 * HomeScreens.init caches. The deps getter returns null until
 * HomeTimeline._initScreensElements has run, so the pre-init guards
 * (width 0 / undefined -> NaN --screens-tv-edge) reproduce HomeScreens'
 * behaviour exactly.
 */
import fastdom from 'fastdom';

import type { TimelineCSSSyncDeps } from 'app/views/home/timeline/timeline.types';
import type { DOMHelper } from 'core/DOMHelper';

import { DepartmentType } from 'app/data/Enums';
import { TextScramble } from 'utils/TextScramble';
import { mapBetween } from 'utils/math';
import DelayHelper from 'utils/timers';

import HomeZonesConfig from 'app/views/home/config/HomeZonesConfig';
import webglHome from 'config/webgl-home';

interface CSSElement {
	id: string;
	$target: HTMLElement;
	min: number;
	max: number;
	cachedWidth?: number; //Measured lazily on first resize — creation literals omit it
	showing: boolean;
	once?: boolean;
	syncX?: boolean;
	relativeX?: number;
	anchorX?: number;
	width?: number;
	parallaxPower?: number;
	hideCallback?: Function;
	xRecord?: number;
}

export class TimelineCSSSync {
	private _dom: DOMHelper;
	private _deps: TimelineCSSSyncDeps;

	private _cssElements: Array<CSSElement> = [];

	//DOM refs the sync writes custom properties onto (subset of the timeline's _domRefs):
	private _$screens: HTMLElement;
	private _$workContainer: HTMLElement;

	//The .ui__hamburger element highlighted by the projects-click UI prompt:
	private _$targetProjectUIButton: Element;
	private _showingProjectsClickUI = false;

	//Explore button label:
	private _screensTextScrable: TextScramble;
	private _spacesButtonLabel: HTMLElement;
	private _screensButtonLabel: HTMLElement;
	private _screensButtonTextShowing: boolean = false;
	private _spacesButtonTextShowing: boolean = false;

	//Frame values sampled from the timeline at the top of each tick():
	private _progress: number;
	private _velocity: number;
	private _containerX: number;
	private _innerWidth: number;
	private _virtualWidth: number;

	private _relativeParallaxX: number;
	private _relativeParallaxXToProgressRatio: number;

	constructor(dom: DOMHelper, deps: TimelineCSSSyncDeps) {
		this._dom = dom;
		this._deps = deps;
	}

	/** Build the CSSElement table from the CORE UI refs overlaid over the home screen */
	public setUIRefs(
		$intro: HTMLElement,
		$screens: HTMLElement,
		$spaces: HTMLElement,
		$dragToExplore: HTMLElement,
		$systems: HTMLElement,
		$workContainerHTML: HTMLElement
	): void {
		this._$screens = $screens;
		this._$workContainer = $workContainerHTML;

		let cssConfig = HomeZonesConfig.computedHomeCssConfig();

		this._cssElements = [
			{
				id: 'intro',
				$target: $intro,
				showing: true,
				min: -0.2,
				max: 0.2,
				syncX: true,
				relativeX: cssConfig.home.relX,
				anchorX: 0.5,
				width: $intro.clientWidth,
				parallaxPower: 0
			},

			{
				id: DepartmentType.SCREENS,
				$target: $screens,
				showing: false,
				min: -1,
				max: 0.1,
				syncX: true,
				relativeX: cssConfig.screens.relX,
				anchorX: 0.5,
				width: 0,
				parallaxPower: 0
			},

			{
				id: DepartmentType.SPACES,
				$target: $spaces,
				showing: false,
				min: 1,
				max: 2,
				syncX: true,
				relativeX: cssConfig.spaces.relX,
				anchorX: 0.5,
				width: $spaces.clientWidth,
				parallaxPower: 0
			},

			{
				id: DepartmentType.SYSTEMS,
				$target: $systems,
				showing: false,
				min: 1,
				max: 2,
				syncX: true,
				relativeX: cssConfig.systems.relX,
				anchorX: 0.5,
				width: $spaces.clientWidth,
				parallaxPower: 0
			}
		];

		this._screensButtonLabel = document.querySelector('.home__explore__area.-screens .home__explore__area__label');
		this._spacesButtonLabel = document.querySelector('.home__explore__area.-spaces .home__explore__area__label');
		//NOTE: constructed but intentionally never triggered during panning -
		//TextScramble's DOM text mutation causes redraw that drops frames on the
		//timeline drag. Scramble is only used where frames aren't critical (intro
		//button, preloader). Don't wire these into the tick path.
		this._screensTextScrable = new TextScramble(this._screensButtonLabel, 15);
		new TextScramble(this._spacesButtonLabel, 15);
	}

	/** Per-frame CSS sync — classes/transforms, explore scramble, projects-click prompt (order preserved) */
	public tick(instant: boolean = false): void {
		this._progress = this._deps.getProgress();
		this._velocity = this._deps.getVelocity();
		this._containerX = this._deps.getContainerX();

		//Size metrics come off the timeline's stable-identity sizes object (ex
		//per-metric getters); snapshot locals kept — same read timing, hot
		//bodies below untouched:
		this._innerWidth = this._deps.sizes.innerWidth;
		this._virtualWidth = this._deps.sizes.virtualWidth;

		//Decide if UI style elements should be shown by setting their class to -show
		this._setCSSClasses(instant);

		//Process the explore text scramble effect:
		this._processExploreButtonHideShow();

		this._hideProjectCLickUi();
	}

	//Cache widths of element: Be careful is Layout Trashing!
	public setWidths(): void {
		this._cssElements.forEach((element) => {
			element.cachedWidth = element.$target.clientWidth;
		});
	}

	//Reset the width of the tracked elements
	public refreshWidths(): void {
		this._cssElements.forEach((css: CSSElement) => {
			if (css.$target.clientWidth) {
				css.width = css.$target.clientWidth;
			}
		});
	}

	public showProjectsClickUI(side = 'screens') {
		let $targetButton =
			side === 'screens'
				? document.querySelector('.home__screens .ui__hamburger')
				: document.querySelector('.home__spaces .ui__hamburger');

		DelayHelper.setTimeout(() => {
			this._showingProjectsClickUI = true;
			this._$targetProjectUIButton = $targetButton;
		}, 1400);

		DelayHelper.setTimeout(() => {
			this._dom.addClass($targetButton, '-show');
		}, 1750);
	}

	private _hideProjectCLickUi() {
		if (this._showingProjectsClickUI && this._progress > 0.01 && this._progress < 0.99) {
			this._dom.removeClass(this._$targetProjectUIButton, '-show');
			this._showingProjectsClickUI = false;
		}
	}

	//Turn off on CSS classes based on their
	//Used to hide show the intro para and the screens / spaces UI
	private _setCSSClasses(instant: boolean = false): void {
		//Compress for 2.5 width timeline

		if (this._innerWidth < webglHome.getSwitchScreensLayoutAt()) {
			let width = this._getTopScreenWidth() - 20;
			if (width < 300) width = 300;
			fastdom.mutate(() => {
				this._$workContainer.style.setProperty('--home-ui-width', `${width}px`);
			});
		}

		let screenX = 0;
		this._cssElements.forEach((element) => {
			if (element.syncX) {
				element.xRecord = Math.round(this._getCSSX(element, instant));

				if (element.id == DepartmentType.SCREENS) {
					screenX = this._getRightScreenX() + 15 - element.xRecord;

					let opacity = 1;

					if (this._innerWidth >= webglHome.getSwitchScreensLayoutAt()) {
						let fadeDown = mapBetween(this._progress, 0, 1, 0.48, 0.63);
						opacity = 1 - fadeDown;
					}

					element.$target.style.opacity = '' + opacity;
				}

				fastdom.mutate(() => {
					element.$target.style.transform = `translate3d(${element.xRecord}px, 0px, 0)`;
					this._$screens.style.setProperty('--screens-tv-edge', `${screenX}px`);
				});
			}

			if (
				!element.showing &&
				!element.once &&
				this._progress > element.min &&
				this._progress < element.max &&
				this._velocity < 0.01
			) {
				this._dom.addClass(element.$target, '-show');
				element.showing = true;
				if (element.id === 'dragToExplore') {
				}
			} else if (element.showing && (this._progress < element.min || this._progress > element.max)) {
				this._dom.removeClass(element.$target, '-show');

				element.showing = false;
				if (element.hideCallback) {
					element.hideCallback(element);
				}
			}
		});
	}

	private _getCSSX(element: CSSElement, instant: boolean = false): number {
		return (
			this._virtualWidth * element.relativeX +
			this._containerX -
			element.anchorX * element.cachedWidth +
			this._calculateParallaxDragOffsetX(element.relativeX, element.parallaxPower, instant)
		);
	}

	//Calculate the parallax offset caused by draging left <> right:
	private _calculateParallaxDragOffsetX(relativeX: number, parallaxPower: number, instant: boolean = false): number {
		//Correct thing to allow for the fact the start/photoshop point of the timeline is center/0.5/home
		this._relativeParallaxX = relativeX - 0.5;
		//Translate this into the current progress
		this._relativeParallaxXToProgressRatio = this._relativeParallaxX - (this._progress - 0.5);
		//Set the offset as percentage of the container width (half the width because of the 0.5 logic).
		return this._relativeParallaxXToProgressRatio * parallaxPower * (this._virtualWidth / 2);
	}

	private _processExploreButtonHideShow(): void {
		let showSpacesUI: boolean = HomeZonesConfig.showUI('spaces', this._progress);
		let showScreensUI: boolean = showSpacesUI ? false : HomeZonesConfig.showUI('screens', this._progress);

		if (!this._screensButtonTextShowing && showScreensUI) {
			this._screensButtonTextShowing = true;
		} else if (this._screensButtonTextShowing && !showScreensUI) {
			this._screensButtonTextShowing = false;
			this._screensTextScrable.setText('');
		}

		if (!this._spacesButtonTextShowing && showSpacesUI) {
			this._spacesButtonTextShowing = true;
		} else if (this._spacesButtonTextShowing && !showSpacesUI) {
			this._spacesButtonTextShowing = false;
		}
	}

	//Screens-TV metrics — read off the same item sprites HomeScreens.init caches
	//(see file header). The guards mirror HomeScreens.getTopScreenWidth /
	//getRightScreenX exactly, including the bare return (undefined + 15 - x
	//-> NaN --screens-tv-edge, ignored by CSS) before the screens elements init.
	private _getTopScreenWidth(): number {
		let sprites = this._deps.getScreensTvSprites();

		if (!sprites || !sprites.topLeft) {
			return 0;
		}

		return sprites.topLeft.width;
	}

	private _getRightScreenX(): number {
		let sprites = this._deps.getScreensTvSprites();

		if (!sprites || !sprites.topRight) {
			return;
		}

		return sprites.topRight.x - sprites.topRight.width / 2;
	}
}
